import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router-dom';
import { isLoggedIn } from '../../../Auth/AuthReducer';

import LayoutFocused from '../LayoutFocused/LayoutFocused';

import styles from './ErrorPage.css';

const messages = defineMessages({
  title: {
    id: 'ErrorPage.Title',
    defaultMessage: 'Something went wrong',
  },
  heading: {
    id: 'ErrorPage.Heading',
    defaultMessage: 'Something went wrong :(',
  },
});

function routeErrorToException(routeError) {
  if (routeError instanceof Error) {
    return routeError;
  }
  if (isRouteErrorResponse(routeError)) {
    const err = new Error(`${routeError.status} ${routeError.statusText}`);
    err.name = 'RouteErrorResponse';
    return err;
  }
  if (typeof routeError === 'string') {
    return new Error(routeError);
  }
  return new Error('Unhandled route error');
}

function ErrorPage(props) {
  const error = useRouteError();
  console.error(error);

  useEffect(() => {
    if (!process.env.CLIENT || !error) {
      return;
    }
    Sentry.withScope(scope => {
      scope.setTag('errorBoundary', 'react-router');
      if (isRouteErrorResponse(error)) {
        scope.setContext('routeError', {
          status: error.status,
          statusText: error.statusText,
          data: error.data,
        });
      }
      Sentry.captureException(routeErrorToException(error));
    });
  }, [error]);

  const { formatMessage } = props.intl;
  return (
    <LayoutFocused className={styles.container} title={formatMessage(messages.title)}>
      <div>
        <h3>
          <FormattedMessage id="ErrorPage.Heading" />
        </h3>
        <p>
          <FormattedMessage
            id="ErrorPage.WeAreWorking"
            defaultMessage="We've been notified and we'll start working to fix the error immediately."
          />
        </p>
        <div className={styles.body}>
          <div className={styles.error}>500</div>
          <div className={styles.alternatives}>
            <FormattedMessage id="ErrorPage.Alternatives" defaultMessage="Now you may want to:" />
            <ul>
              <li>
                <FormattedMessage
                  id="ErrorPage.SendEmail"
                  defaultMessage="Send us an {email}"
                  values={{
                    email: (
                      <Link to="mailto:hello@promoter.ninja">
                        <FormattedMessage id="ErrorPage.Email" defaultMessage="email" />
                      </Link>
                    ),
                  }}
                />
              </li>
              {props.loggedIn ? (
                <li>
                  <FormattedMessage
                    id="ErrorPage.GoToStart"
                    defaultMessage="Go to the {startingPage}."
                    values={{
                      startingPage: (
                        <Link to="/">
                          <FormattedMessage
                            id="ErrorPage.StartingPage"
                            defaultMessage="starting page"
                          />
                        </Link>
                      ),
                    }}
                  />
                </li>
              ) : (
                <li>
                  <FormattedMessage
                    id="ErrorPage.TryOurService"
                    defaultMessage="{knowMore} about Promoter Ninja and sign up for free."
                    values={{
                      knowMore: (
                        <Link to="/">
                          <FormattedMessage id="ErrorPage.KnowMore" defaultMessage="Know more" />
                        </Link>
                      ),
                    }}
                  />
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </LayoutFocused>
  );
}

ErrorPage.propTypes = {
  loggedIn: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  loggedIn: isLoggedIn(state),
});

export default connect(mapStateToProps)(injectIntl(ErrorPage));
